import React, { useEffect } from 'react';

import { Button, Fab, Input, Modal, VStack, HStack, Box } from 'native-base';
import MaterialIconByName from '../MaterialIconByName';
import { useSelector } from 'react-redux';
import { IStore } from 'src/redux/reducers';
import { IManualJobEntryInput, isDoubleSidedScanner } from 'src/models';
import { isAppOrWindows } from 'src/utils/utils';
import { inputStyle } from 'src/styles/inputStyle';
import { getText } from 'src/utils/i18n';
import { useResponsive } from 'src/hooks/useResponsive';
import { isLightboxFeatureEnabled } from 'src/utils/grainFeatureUtils';
import { getOperatorCaptureMode } from 'src/utils/operatorMode';
import { isElectron } from 'src/utils/miscUtils';

interface FabButtonProps {
    isDisabled?: boolean;
    captureImageClicked: () => void;
    imageUploadClicked: () => void;
    scanImageClicked: () => void;
    connectScannerClicked: () => void;
    doubleSidedImageUploadClicked: () => void;
    manualEntryClicked: (payload: IManualJobEntryInput) => void;
}

export default function FabButtons(props: FabButtonProps) {
    const { isLargeScreen } = useResponsive();

    const scannerId = useSelector((store: IStore) => store.user.userProfile.response?.settings.scanner_id);
    const subscriptions = useSelector((store: IStore) => store.user.userProfile.response?.subscriptions) || [];
    const profileId = useSelector((store: IStore) => store.grainProfile.profileId);
    const profiles = useSelector((store: IStore) => store.grainProfile.grainProfiles) || [];
    const scannerConnected = useSelector((store: IStore) => Boolean(store.bluetooth.connectedRpi.connectedDevice));

    const lightBoxEnabled = useSelector((store: IStore) =>
        isLightboxFeatureEnabled(store.user.userProfile.response?.settings)
    );
    const manualEntryEnabled = useSelector((store: IStore) => store.user.userProfile.response?.settings.manual_entry_enabled) || false;
    const selectedProfile = profiles.find(profile => profile.profileId === profileId);
    const captureMode = getOperatorCaptureMode({ selectedProfile, subscriptions });
    const cameraModeEnabled = captureMode === 'CAMERA' || lightBoxEnabled;
    const scanImageAvailable = scannerConnected || isElectron();

    const [isManualEntryModalVisible, setIsManualEntryModalVisible] = React.useState(false);
    const [numberOfKernels, setNumberOfKernels] = React.useState("0");

    const [isOpen, setIsOpen] = React.useState(false);

    const [fabConfig, setFabConfig] = React.useState({
        captureImage: {
            enabled: false,
            bottom: 253
        },
        scanImage: {
            enabled: false,
            bottom: 135
        },
        manualEntry: {
            enabled: false,
            bottom: 194
        },
        galleryImage: {
            enabled: true,
            bottom: 76
        },
    });

    useEffect(() => {
        let bottom = 76;

        const newFabConfig = {
            captureImage: {
                enabled: false,
                bottom: 253
            },
            scanImage: {
                enabled: false,
                bottom: 135
            },
            manualEntry: {
                enabled: false,
                bottom: 194
            },
            galleryImage: {
                enabled: !cameraModeEnabled,
                bottom: 76
            },
        };

        if (!cameraModeEnabled && manualEntryEnabled) {
            newFabConfig.manualEntry.enabled = true;
            newFabConfig.manualEntry.bottom = bottom + 59;
            bottom = bottom + 59;
        }

        if (!cameraModeEnabled && isAppOrWindows()) {
            newFabConfig.scanImage.enabled = true;
            newFabConfig.scanImage.bottom = bottom + 59;
            bottom = bottom + 59;
        }

        if (cameraModeEnabled && isAppOrWindows()) {
            newFabConfig.captureImage.enabled = true;
            newFabConfig.captureImage.bottom = bottom + 59;
            bottom = bottom + 59;
        }

        setFabConfig(newFabConfig);

    }, [cameraModeEnabled, manualEntryEnabled]);

    // Bottom bar for large screens
    if (isLargeScreen) {
        return (
            <>
                <Modal
                    size={"lg"}
                    isOpen={isManualEntryModalVisible}>
                    <Modal.Content>
                        <Modal.CloseButton />
                        <Modal.Header>Sample Details</Modal.Header>
                        <Modal.Body>
                            <VStack p="2" space={4}>
                                <Input
                                    key={"numberOfKernels"}
                                    style={inputStyle.measurementInput}
                                    placeholder="Number of Kernels"
                                    onChangeText={(text) => {
                                        setNumberOfKernels(text)
                                    }}
                                    value={numberOfKernels} />
                            </VStack>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button.Group space={2}>
                                <Button variant="ghost" colorScheme="blueGray" onPress={() => {
                                    setIsManualEntryModalVisible(false)
                                }}>
                                    Cancel
                                </Button>
                                <Button
                                    onPress={async () => {
                                        setIsManualEntryModalVisible(false)
                                        props.manualEntryClicked({
                                            grain_count: numberOfKernels
                                        });
                                    }
                                    }>Create Manual Entry</Button>
                            </Button.Group>
                        </Modal.Footer>
                    </Modal.Content>
                </Modal>
                
                {/* Bottom Bar for Large Screens */}
                <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    bg="white"
                    borderTopWidth={1}
                    borderTopColor="gray.200"
                    px={6}
                    py={3}
                    shadow={6}
                    _dark={{
                        bg: "gray.800",
                        borderTopColor: "gray.700"
                    }}
                >
                    <HStack space={3} justifyContent="center" flexWrap="wrap">
                        {fabConfig.galleryImage.enabled && (
                            <Button
                                size="md"
                                leftIcon={<MaterialIconByName name="image-area" />}
                                isDisabled={props.isDisabled}
                                onPress={() => {
                                    if (isDoubleSidedScanner(scannerId)) {
                                        props.doubleSidedImageUploadClicked();
                                    } else {
                                        props.imageUploadClicked();
                                    }
                                }}
                            >
                                {isDoubleSidedScanner(scannerId)
                                    ? getText('chooseImagesDs')
                                    : getText('chooseImage')}
                            </Button>
                        )}
                        
                        {fabConfig.scanImage.enabled && (
                            <Button
                                size="md"
                                leftIcon={<MaterialIconByName name="scanner" />}
                                isDisabled={props.isDisabled}
                                onPress={scanImageAvailable ? props.scanImageClicked : props.connectScannerClicked}
                            >
                                {scanImageAvailable ? getText('scanImage') : getText('connectScanner')}
                            </Button>
                        )}

                        {fabConfig.captureImage.enabled && (
                            <Button
                                size="md"
                                leftIcon={<MaterialIconByName name="camera" />}
                                isDisabled={props.isDisabled}
                                onPress={props.captureImageClicked}
                            >
                                {getText('takePhoto')}
                            </Button>
                        )}

                        {fabConfig.manualEntry.enabled && (
                            <Button
                                size="md"
                                leftIcon={<MaterialIconByName name="pencil" />}
                                isDisabled={props.isDisabled}
                                onPress={() => setIsManualEntryModalVisible(true)}
                            >
                                {getText('manualEntry')}
                            </Button>
                        )}
                    </HStack>
                </Box>
            </>
        );
    }

    // Original FAB for smaller screens
    return (
        <>
            <Modal
                size={"lg"}
                isOpen={isManualEntryModalVisible}>
                <Modal.Content>
                    <Modal.CloseButton />
                    <Modal.Header>Sample Details</Modal.Header>
                    <Modal.Body>
                        <VStack p="2" space={4}>
                            <Input
                                key={"numberOfKernels"}
                                style={inputStyle.measurementInput}
                                placeholder="Number of Kernels"
                                onChangeText={(text) => {
                                    setNumberOfKernels(text)
                                }}
                                value={numberOfKernels} />
                        </VStack>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button.Group space={2}>
                            <Button variant="ghost" colorScheme="blueGray" onPress={() => {
                                setIsManualEntryModalVisible(false)
                            }}>
                                Cancel
                            </Button>
                            <Button
                                onPress={async () => {
                                    setIsManualEntryModalVisible(false)
                                    props.manualEntryClicked({
                                        grain_count: numberOfKernels
                                    });
                                }
                                }>Create Manual Entry</Button>
                        </Button.Group>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>
            <VStack>
                {
                    isOpen && <>
                        {
                            fabConfig.captureImage.enabled &&
                            <Fab
                                renderInPortal={false}
                                shadow={2}
                                size="sm"
                                onPress={() => {
                                    setIsOpen(!isOpen);
                                    props.captureImageClicked();
                                }}
                                bottom={fabConfig.captureImage.bottom}
                                right={5}
                                label={getText('takePhoto')}
                                disabled={props.isDisabled}
                                icon={<MaterialIconByName name="camera" />} />
                        }
                        {
                            fabConfig.manualEntry.enabled &&
                            <Fab
                                renderInPortal={false}
                                shadow={2}
                                size="sm"
                                onPress={() => {
                                    setIsOpen(!isOpen);
                                    setIsManualEntryModalVisible(true);
                                }}
                                bottom={fabConfig.manualEntry.bottom}
                                right={5}
                                label={getText('manualEntry')}
                                disabled={props.isDisabled}
                                icon={<MaterialIconByName name="pencil" />} />
                        }
                        {
                            fabConfig.scanImage.enabled &&
                            <Fab
                                renderInPortal={false}
                                shadow={2}
                                size="sm"
                                onPress={() => {
                                    setIsOpen(!isOpen);
                                    if (scanImageAvailable) {
                                        props.scanImageClicked();
                                    } else {
                                        props.connectScannerClicked();
                                    }
                                }}
                                bottom={fabConfig.scanImage.bottom}
                                right={5}
                                label={scanImageAvailable ? getText('scanImage') : getText('connectScanner')}
                                disabled={props.isDisabled}
                                icon={<MaterialIconByName name="scanner" />} />
                        }

                        {
                            fabConfig.galleryImage.enabled &&
                            <Fab
                                renderInPortal={false}
                                shadow={2}
                                size="sm"
                                onPress={() => {
                                    setIsOpen(!isOpen);

                                    if (isDoubleSidedScanner(scannerId)) {
                                        props.doubleSidedImageUploadClicked();
                                    } else {
                                        props.imageUploadClicked();
                                    }
                                }}
                                bottom={fabConfig.galleryImage.bottom}
                                right={5}
                                label={isDoubleSidedScanner(scannerId)
                                    ? getText('chooseImagesDs')
                                    : getText('chooseImage')}
                                disabled={props.isDisabled}
                                icon={<MaterialIconByName name="image-area" />} />
                        }
                    </>
                }

                <Fab
                    renderInPortal={false}
                    shadow={2}
                    size="lg"
                    onPress={() => {
                        setIsOpen(!isOpen);
                    }}
                    icon={<MaterialIconByName name={isOpen ? "close" : "plus"} />} />
            </VStack>
        </>
    );
}
